import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../supabase";
import Header from "../components/ui/Header";
import { Link } from "react-router-dom";
import { useOrganization } from "../contexts/OrganizationContext";

function ShiftCompatibilitySettings() {
  const { currentOrganization } = useOrganization();
  const [staff, setStaff] = useState([]);
  const [patterns, setPatterns] = useState([]);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);

  // 現在選択中の職員ペア（staff.id）
  const [selectedPair, setSelectedPair] = useState({ userA: "", userB: "" });

  // スタッフ検索キーワード（A/B 別）
  const [searchA, setSearchA] = useState("");
  const [searchB, setSearchB] = useState("");

  // 選択中のペアに対するルール（未保存）
  const [pairRules, setPairRules] = useState([]);

  // 新しいルールのフォーム状態
  //   scope === 'sameShift' のとき restrictedGroups を使う
  //   scope === 'sameRoom'  のとき restrictedGroups は空でよい（全勤務対象）
  const [newRule, setNewRule] = useState({
    scope: "sameShift",
    restrictedGroups: []
  });

  // エラー表示用state
  const [errors, setErrors] = useState({});

  // スタイル定義
  const styles = {
    container: { 
      padding: "2rem", 
      fontFamily: "sans-serif",
      maxWidth: "1000px",
      margin: "0 auto"
    },
    backLink: {
      display: "inline-block",
      marginBottom: "1rem",
      color: "#007bff",
      textDecoration: "none",
      fontWeight: "500"
    },
    formCard: {
      marginTop: "1.5rem",
      background: "#ffffff",
      padding: "1.5rem",
      borderRadius: "8px",
      boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
      border: "1px solid #e0e0e0"
    },
    formTitle: {
      borderBottom: "1px solid #e0e0e0",
      paddingBottom: "0.75rem",
      marginTop: 0,
      color: "#333"
    },
    formRow: {
      display: "flex",
      flexWrap: "wrap",
      gap: "1rem",
      marginBottom: "1.25rem",
      alignItems: "center"
    },
    formLabel: {
      fontWeight: "500",
      display: "block",
      marginBottom: "0.5rem",
      color: "#333"
    },
    select: {
      padding: "0.5rem",
      borderRadius: "4px",
      border: "1px solid #d0d0d0",
      flex: "1",
      minWidth: "200px"
    },
    multiSelect: {
      padding: "0.5rem",
      borderRadius: "4px",
      border: "1px solid #d0d0d0",
      flex: "1",
      minWidth: "200px",
      height: "120px"
    },
    button: {
      padding: "0.65rem 1.25rem",
      backgroundColor: "#007bff",
      color: "white",
      border: "none",
      borderRadius: "4px",
      cursor: "pointer",
      fontWeight: "500",
      transition: "background-color 0.2s"
    },
    secondaryButton: {
      padding: "0.5rem 1rem",
      backgroundColor: "#6c757d",
      color: "white",
      border: "none",
      borderRadius: "4px",
      cursor: "pointer",
      marginRight: "0.5rem"
    },
    dangerButton: {
      padding: "0.5rem 1rem",
      backgroundColor: "#dc3545",
      color: "white",
      border: "none",
      borderRadius: "4px",
      cursor: "pointer"
    },
    ruleCard: {
      padding: "1rem",
      backgroundColor: "#f8f9fa",
      borderRadius: "6px",
      marginBottom: "1rem",
      border: "1px solid #e0e0e0",
      position: "relative"
    },
    deleteButton: {
      position: "absolute",
      top: "0.5rem",
      right: "0.5rem",
      background: "none",
      border: "none",
      color: "#dc3545",
      cursor: "pointer",
      fontSize: "1.25rem"
    },
    existingRuleCard: {
      marginBottom: "1rem",
      border: "1px solid #e0e0e0",
      padding: "1rem",
      borderRadius: "6px",
      backgroundColor: "#fff",
      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center"
    },
    ruleDetails: {
      flex: "1"
    },
    rulesGrid: {
      display: "grid",
      gap: "1rem",
    },
    errorText: {
      color: "#dc3545",
      fontSize: "0.875rem",
      marginTop: "0.25rem"
    },
    badge: {
      display: "inline-block",
      padding: "0.25rem 0.5rem",
      backgroundColor: "#e9ecef",
      borderRadius: "30px",
      fontSize: "0.875rem",
      marginRight: "0.5rem",
      marginBottom: "0.5rem"
    },
    conditionBadge: {
      display: "inline-block",
      padding: "0.35rem 0.75rem",
      backgroundColor: "#cfe2ff",
      color: "#0a58ca",
      borderRadius: "30px",
      fontSize: "0.875rem",
      marginRight: "0.5rem",
      marginBottom: "0.5rem",
      fontWeight: "500"
    },
    pairPanel: {
      padding: "1rem",
      backgroundColor: "#e3f2fd",
      borderRadius: "6px",
      marginBottom: "1.5rem"
    },
    pairHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "1rem"
    },
    pairName: {
      fontSize: "1.1rem",
      fontWeight: "bold",
      color: "#0d47a1"
    },
    infoText: {
      fontSize: "0.9rem",
      color: "#666",
      marginTop: "0.5rem"
    }
  };

  useEffect(() => {
    if (!currentOrganization?.id) return;
    const orgId = currentOrganization.id;
    const fetchData = async () => {
      setLoading(true);
      try {
        const [staffRes, patternRes, ruleRes] = await Promise.all([
          supabase
            .from('staff')
            .select('id, employee_id, last_name, first_name, is_active')
            .eq('organization_id', orgId)
            .eq('is_active', true)
            .order('employee_id', { ascending: true }),
          supabase
            .from('shift_patterns')
            .select('id, name, short_name, priority, configuration')
            .eq('organization_id', orgId)
            .eq('is_active', true)
            .order('priority', { ascending: true }),
          supabase
            .from('shift_incompatibilities')
            .select('*')
            .eq('organization_id', orgId)
            .order('created_at', { ascending: false }),
        ]);
        if (staffRes.error) throw staffRes.error;
        if (patternRes.error) throw patternRes.error;
        if (ruleRes.error) throw ruleRes.error;

        // 互換のため UI 側のキー名（lastName/firstName/employeeId）にマップ
        setStaff(
          (staffRes.data || []).map((s) => ({
            id: s.id,
            employeeId: s.employee_id,
            lastName: s.last_name || '',
            firstName: s.first_name || '',
          }))
        );
        setPatterns(
          (patternRes.data || []).map((p) => ({
            id: p.id,
            name: p.name || p.short_name || p.id,
            requestGroup: p.configuration?.request_group || '',
            requestGroupLabel: p.configuration?.request_group_label || '',
          }))
        );
        setRules(
          (ruleRes.data || []).map((r) => ({
            id: r.id,
            userA: r.user_a_staff_id,
            userB: r.user_b_staff_id,
            scope: r.scope,
            restrictedGroups: Array.isArray(r.restricted_request_groups)
              ? r.restricted_request_groups
              : [],
            createdAt: r.created_at,
          }))
        );
      } catch (error) {
        console.error("データ取得エラー:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentOrganization?.id]);

  // 希望グループ一覧（patterns から distinct で導出）
  //   各 group は { key, label, patternNames } の形で扱う
  const requestGroups = useMemo(() => {
    const map = new Map();
    patterns.forEach((p) => {
      const key = (p.requestGroup || '').trim();
      if (!key) return;
      if (!map.has(key)) {
        map.set(key, {
          key,
          label: p.requestGroupLabel || key,
          patternNames: [],
        });
      }
      map.get(key).patternNames.push(p.name);
      // ラベルが空のパターンが混ざる場合、ラベル付きを優先
      if (p.requestGroupLabel && !map.get(key).label) {
        map.get(key).label = p.requestGroupLabel;
      }
    });
    return Array.from(map.values()).sort((a, b) => a.key.localeCompare(b.key));
  }, [patterns]);

  // スタッフ検索フィルタ
  const filterStaff = (keyword) => {
    const kw = (keyword || '').trim().toLowerCase();
    if (!kw) return staff;
    return staff.filter((s) => {
      const fullName = `${s.lastName} ${s.firstName}`.trim().toLowerCase();
      return (
        fullName.includes(kw)
        || (s.employeeId || '').toLowerCase().includes(kw)
      );
    });
  };
  const staffOptionsA = useMemo(() => filterStaff(searchA), [searchA, staff]); // eslint-disable-line react-hooks/exhaustive-deps
  const staffOptionsB = useMemo(() => filterStaff(searchB), [searchB, staff]); // eslint-disable-line react-hooks/exhaustive-deps

  // ペア選択を設定
  const handleSetPair = () => {
    if (!selectedPair.userA || !selectedPair.userB) {
      setErrors({ pair: "両方の職員を選択してください" });
      return;
    }
    
    if (selectedPair.userA === selectedPair.userB) {
      setErrors({ pair: "同じ職員を選択することはできません" });
      return;
    }
    
    setErrors({});
    // 既にルールが追加されている場合はクリア
    setPairRules([]);
  };

  // ルールを追加
  const addRule = () => {
    // sameShift は希望グループの選択が必須、sameRoom は不要
    if (newRule.scope === 'sameShift' && newRule.restrictedGroups.length === 0) {
      setErrors({ rule: "少なくとも1つの希望グループを選択してください" });
      return;
    }

    setErrors({});

    // sameRoom のときは保存値を空にする
    const ruleToAdd =
      newRule.scope === 'sameRoom'
        ? { scope: 'sameRoom', restrictedGroups: [] }
        : { ...newRule };

    setPairRules([...pairRules, ruleToAdd]);

    setNewRule({
      scope: "sameShift",
      restrictedGroups: []
    });
  };

  // ルールを削除
  const removeRule = (index) => {
    setPairRules(pairRules.filter((_, i) => i !== index));
  };

  // ルールをデータベースに保存
  const saveRules = async () => {
    if (pairRules.length === 0) {
      setErrors({ save: "少なくとも1つのルールを追加してください" });
      return;
    }
    if (!currentOrganization?.id) {
      setErrors({ save: "組織情報が読み込まれていません" });
      return;
    }
    setErrors({});

    try {
      const rows = pairRules.map((rule) => ({
        organization_id: currentOrganization.id,
        user_a_staff_id: selectedPair.userA,
        user_b_staff_id: selectedPair.userB,
        scope: rule.scope,
        restricted_request_groups:
          rule.scope === 'sameRoom' ? [] : (rule.restrictedGroups || []),
      }));
      const { error: insertError } = await supabase
        .from('shift_incompatibilities')
        .insert(rows);
      if (insertError) throw insertError;

      setPairRules([]);
      setSelectedPair({ userA: "", userB: "" });

      // ルール一覧を再取得
      const { data, error: fetchError } = await supabase
        .from('shift_incompatibilities')
        .select('*')
        .eq('organization_id', currentOrganization.id)
        .order('created_at', { ascending: false });
      if (fetchError) throw fetchError;
      setRules(
        (data || []).map((r) => ({
          id: r.id,
          userA: r.user_a_staff_id,
          userB: r.user_b_staff_id,
          scope: r.scope,
          restrictedGroups: Array.isArray(r.restricted_request_groups)
            ? r.restricted_request_groups
            : [],
          createdAt: r.created_at,
        }))
      );
    } catch (error) {
      console.error("ルール追加エラー:", error);
      setErrors({ save: `ルールの追加に失敗しました: ${error.message}` });
    }
  };

  // 既存ルール削除処理
  const handleDelete = async (id) => {
    if (!window.confirm("このルールを削除してもよろしいですか？")) return;
    try {
      const { error } = await supabase
        .from('shift_incompatibilities')
        .delete()
        .eq('id', id)
        .eq('organization_id', currentOrganization?.id);
      if (error) throw error;
      setRules((prev) => prev.filter((r) => r.id !== id));
    } catch (error) {
      console.error("削除エラー:", error);
      alert(`削除に失敗しました: ${error.message}`);
    }
  };

  // スタッフ名を取得（staffId は staff.id）
  const getStaffName = (staffId) => {
    const staffMember = staff.find((s) => s.id === staffId);
    return staffMember
      ? `${staffMember.lastName} ${staffMember.firstName}`.trim() || staffMember.employeeId
      : staffId;
  };

  // 希望グループのラベルを取得
  const getRequestGroupLabel = (key) => {
    const g = requestGroups.find((rg) => rg.key === key);
    return g ? g.label : key;
  };

  // スコープの表示名を取得
  const getScopeDisplayName = (scope) => {
    switch (scope) {
      case "sameShift":
        return "同じ希望グループの勤務で被らない";
      case "sameRoom":
        return "同じ部屋に配置されない（全勤務対象）";
      default:
        return scope;
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <Header />
        <h2>相性設定</h2>
        <p>読み込み中...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <Header />
      <h2>シフト相性設定</h2>
      <Link to="/settings" style={styles.backLink}>← 設定メニューへ戻る</Link>
      
      <div style={styles.formCard}>
        <h3 style={styles.formTitle}>新しい相性ルールの追加</h3>
        
        {/* 職員ペア選択部分 */}
        <div style={styles.formRow}>
          <div style={{ flex: 1, minWidth: "240px" }}>
            <label style={styles.formLabel}>職員A：</label>
            <input
              type="text"
              placeholder="名前 / 社員番号で検索"
              value={searchA}
              onChange={(e) => setSearchA(e.target.value)}
              style={{ ...styles.select, marginBottom: "0.4rem" }}
            />
            <select
              value={selectedPair.userA}
              onChange={(e) => setSelectedPair({ ...selectedPair, userA: e.target.value })}
              style={{ ...styles.select, height: "180px" }}
              size={8}
            >
              <option value="">--選択--</option>
              {staffOptionsA.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.employeeId}: {s.lastName} {s.firstName}
                </option>
              ))}
            </select>
            <div style={styles.infoText}>{staffOptionsA.length} 名</div>
          </div>

          <div style={{ flex: 1, minWidth: "240px" }}>
            <label style={styles.formLabel}>職員B：</label>
            <input
              type="text"
              placeholder="名前 / 社員番号で検索"
              value={searchB}
              onChange={(e) => setSearchB(e.target.value)}
              style={{ ...styles.select, marginBottom: "0.4rem" }}
            />
            <select
              value={selectedPair.userB}
              onChange={(e) => setSelectedPair({ ...selectedPair, userB: e.target.value })}
              style={{ ...styles.select, height: "180px" }}
              size={8}
            >
              <option value="">--選択--</option>
              {staffOptionsB.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.employeeId}: {s.lastName} {s.firstName}
                </option>
              ))}
            </select>
            <div style={styles.infoText}>{staffOptionsB.length} 名</div>
          </div>

          <div style={{ alignSelf: "flex-end" }}>
            <button
              type="button"
              onClick={handleSetPair}
              style={styles.button}
            >
              ペアを設定
            </button>
          </div>
        </div>
        
        {errors.pair && <div style={styles.errorText}>{errors.pair}</div>}
        
        {/* 選択されたペアに対するルール追加部分 */}
        {selectedPair.userA && selectedPair.userB && (
          <div style={styles.pairPanel}>
            <div style={styles.pairHeader}>
              <div style={styles.pairName}>
                {getStaffName(selectedPair.userA)} と {getStaffName(selectedPair.userB)} の相性ルール
              </div>
            </div>
            
            <div style={styles.formRow}>
              <div style={{ flex: 1 }}>
                <label style={styles.formLabel}>適用スコープ：</label>
                <select
                  value={newRule.scope}
                  onChange={(e) => setNewRule({ ...newRule, scope: e.target.value, restrictedGroups: [] })}
                  style={styles.select}
                >
                  <option value="sameShift">同じ希望グループの勤務で被らない</option>
                  <option value="sameRoom">同じ部屋に配置されない（全勤務対象）</option>
                </select>
              </div>
            </div>

            {newRule.scope === 'sameShift' ? (
              <div style={styles.formRow}>
                <div style={{ flex: 1 }}>
                  <label style={styles.formLabel}>対象の希望グループ：</label>
                  {requestGroups.length === 0 ? (
                    <div style={styles.infoText}>
                      希望グループが設定されたパターンがありません。<br />
                      勤務パターン設定で「希望グループ」を入力してください。
                    </div>
                  ) : (
                    <>
                      <select
                        multiple
                        value={newRule.restrictedGroups}
                        onChange={(e) => setNewRule({
                          ...newRule,
                          restrictedGroups: Array.from(e.target.selectedOptions).map((o) => o.value)
                        })}
                        style={styles.multiSelect}
                      >
                        {requestGroups.map((g) => (
                          <option key={g.key} value={g.key}>
                            {g.label}（{g.patternNames.join(' / ')}）
                          </option>
                        ))}
                      </select>
                      {errors.rule && <div style={styles.errorText}>{errors.rule}</div>}
                      <div style={styles.infoText}>
                        複数選択する場合は Ctrl/⌘ を押しながらクリックしてください
                      </div>
                    </>
                  )}
                </div>

                <div style={{ alignSelf: "flex-end" }}>
                  <button
                    type="button"
                    onClick={addRule}
                    style={styles.secondaryButton}
                  >
                    + ルールを追加
                  </button>
                </div>
              </div>
            ) : (
              <div style={styles.formRow}>
                <div style={{ flex: 1 }}>
                  <div style={styles.infoText}>
                    どの勤務であっても、この2名は同じ部屋に同時配置されません。
                    （勤務パターンの指定は不要）
                  </div>
                </div>
                <div style={{ alignSelf: "flex-end" }}>
                  <button
                    type="button"
                    onClick={addRule}
                    style={styles.secondaryButton}
                  >
                    + ルールを追加
                  </button>
                </div>
              </div>
            )}
            
            {/* 追加されたルール一覧 */}
            {pairRules.length > 0 && (
              <div style={{ marginTop: "1rem" }}>
                <h4>追加したルール</h4>
                {pairRules.map((rule, index) => (
                  <div key={index} style={styles.ruleCard}>
                    <button 
                      type="button" 
                      onClick={() => removeRule(index)}
                      style={styles.deleteButton}
                      title="このルールを削除"
                    >
                      ×
                    </button>
                    
                    <div>
                      <div style={{ marginBottom: "0.5rem" }}>
                        <span style={styles.conditionBadge}>スコープ</span>
                        {getScopeDisplayName(rule.scope)}
                      </div>

                      {rule.scope === 'sameShift' && (
                        <div>
                          <span style={styles.conditionBadge}>対象希望グループ</span>
                          {(rule.restrictedGroups || []).map((g) => (
                            <span key={g} style={styles.badge}>{getRequestGroupLabel(g)}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                
                <div style={{ textAlign: "right", marginTop: "1rem" }}>
                  <button 
                    type="button" 
                    onClick={saveRules}
                    style={styles.button}
                  >
                    全てのルールを保存
                  </button>
                </div>
                
                {errors.save && <div style={styles.errorText}>{errors.save}</div>}
              </div>
            )}
          </div>
        )}
        
        <div style={{ fontSize: "0.9rem", color: "#666", marginTop: "1rem" }}>
          <p><strong>相性ルールの追加方法:</strong></p>
          <p>1. 相性ルールを設定する職員ペア（職員AとB）を検索＋選択し「ペアを設定」をクリック</p>
          <p>2. 適用スコープを選択：</p>
          <p style={{ marginLeft: "1.2rem" }}>・「同じ希望グループの勤務で被らない」→ 対象の希望グループを選択（例：夜勤グループ）</p>
          <p style={{ marginLeft: "1.2rem" }}>・「同じ部屋に配置されない」→ どの勤務でも同室配置されません（追加指定不要）</p>
          <p>3. 「+ ルールを追加」→ 同じペアに複数追加可</p>
          <p>4. 「全てのルールを保存」をクリックして設定を確定</p>
        </div>
      </div>

      <h3 style={{ marginTop: "2rem" }}>登録済みの相性ルール ({rules.length}件)</h3>
      
      {rules.length === 0 ? (
        <p>登録されているルールはありません</p>
      ) : (
        <div style={styles.rulesGrid}>
          {rules.map((rule) => (
            <div key={rule.id} style={styles.existingRuleCard}>
              <div style={styles.ruleDetails}>
                <div style={{ marginBottom: "0.5rem" }}>
                  <span style={styles.conditionBadge}>職員ペア</span>
                  <strong>{getStaffName(rule.userA)}</strong> と <strong>{getStaffName(rule.userB)}</strong>
                </div>
                
                <div style={{ marginBottom: "0.5rem" }}>
                  <span style={styles.conditionBadge}>スコープ</span>
                  {getScopeDisplayName(rule.scope)}
                </div>

                {rule.scope === 'sameShift' && (
                  <div>
                    <span style={styles.conditionBadge}>対象希望グループ</span>
                    {(rule.restrictedGroups || []).map((g) => (
                      <span key={g} style={styles.badge}>{getRequestGroupLabel(g)}</span>
                    ))}
                  </div>
                )}
              </div>
              
              <button 
                type="button"
                onClick={() => handleDelete(rule.id)}
                style={styles.dangerButton}
              >
                削除
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ShiftCompatibilitySettings;